import { CartBrowsingCriteria, RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { makeAutoObservable, runInAction } from 'mobx';
import GroupModule from 'store/modules/group/groupModule';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';
import { getEffectiveDatesOptions } from 'global/services-helpers/domainService';
import { updateGroup } from 'api/operations/groupService';
import { redirectToRoute } from 'routes/redirectToRoute';
import { RouteV2Id } from 'routes/routesConfigV2';
import { getAllEnrollmentPeriodsByGroupId, initiateProposal } from 'api/operations/enrollmentService';
import { createCart } from 'api/operations/cartServiceV2';
import { EnrollmentPeriod, EnrollmentStage, InitiateType } from 'types/enrollmentService';
import { getCoveredProductLines } from 'global/services-helpers/enrollmentService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { DomainProductLinesDelegate } from 'store/modules/initiate-quote/domainProductLinesDelegate';
import { EffectiveDateConfigItem } from 'store/modules/initiate-quote/types';
import { InitiateQuoteFlowGroupInfoDelegate } from './initiateQuoteFlowGroupInfoDelegate';

/**
 * TO_DO_QUOTE_CHECKLIST:
 * Unit tests scope:
 * initiateQuoteFlow store & pages;
 */

export interface DomainParams {
  groupId: string;
  nextPageId: RouteV2Id;
}

export interface InitiateQuoteFlowParams {
  domainParams: DomainParams;
}

export interface SaveProposalInput {
  proposalName: string;
  effectiveDate: string;
  rateSegment: RateSegment;
  browsingCriteria: CartBrowsingCriteria;
  initiateFormAnswers?: Record<string, string>;
}

/**
 * Flow delegate for default and sponsor-specific quote initiation. Serves as the starting point for proposal creation
 */
export default class InitiateQuoteFlowDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private _domainData: DomainParams;
  private _groupModule: GroupModule;
  private _enrollmentPeriods: EnrollmentPeriod[] = [];
  private _isInitialDataLoading = false;
  private _cartId = '';
  private _periodId = '';
  public groupInfoDelegate: InitiateQuoteFlowGroupInfoDelegate;

  public get quoteData(): { cartId: string; periodId: string } {
    return { cartId: this._cartId, periodId: this._periodId };
  }

  /**
   * Product-line chips and quotability for this flow (GROUP). The API payload is stored in full
   * so Altus Continue can use any rate segment.

   */
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;

  public get domainData(): DomainParams {
    return this._domainData;
  }

  public get effectiveDatesConfig(): EffectiveDateConfigItem[] {
    const { effectiveDates, canBrowseInstalledEffectiveDate } = this._rootStore.domainStoreV2;

    return getEffectiveDatesOptions(effectiveDates).map((option) => {
      const stage = this._enrollmentPeriods.find((period) => period.effectiveDate === option.value)?.stage ?? null;

      return {
        ...option,
        stage,
        isDisabled: stage === EnrollmentStage.INSTALLED && !canBrowseInstalledEffectiveDate,
      };
    });
  }

  public isRenewalEffectiveDate(effectiveDate: string): boolean {
    return this._enrollmentPeriods.some(
      (period) => period.effectiveDate === effectiveDate && period.stage === EnrollmentStage.RENEWAL,
    );
  }

  public get enrollmentPeriods(): EnrollmentPeriod[] {
    return this._enrollmentPeriods;
  }

  /** Loading driven by GroupModule (e.g. group fetch). */
  public get isFlowDataLoading(): boolean {
    return this._groupModule.isLoading;
  }

  public get groupModule(): GroupModule {
    return this._groupModule;
  }

  /** Used for the initial page content loader */
  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading || this.isFlowDataLoading;
  }

  public setIsInitialDataLoading(isInitialDataLoading: boolean): void {
    this._isInitialDataLoading = isInitialDataLoading;
  }

  public async getFlowInitialData(): Promise<void> {
    const [enrollmentPeriods] = await Promise.all([
      getAllEnrollmentPeriodsByGroupId(this._domainData.groupId),
      this._groupModule.getGroup(this._domainData.groupId),
    ]);

    runInAction(() => {
      this._enrollmentPeriods = enrollmentPeriods ?? [];
    });
  }

  public async createCartAndSaveProposal({
    effectiveDate,
    proposalName,
    rateSegment,
    browsingCriteria,
    initiateFormAnswers,
  }: SaveProposalInput): Promise<void> {
    const cart = await createCart({
      groupId: this.domainData.groupId,
      name: proposalName,
      rateSegment,
      cartSource: 'AGENT',
      browsingCriteria,
    });

    await runInAction(async () => {
      this._cartId = cart.id;
      const [period] = await Promise.all([
        initiateProposal(
          {
            groupId: this.domainData.groupId,
            cartName: proposalName,
            cartId: this._cartId,
            effectiveDate,
            productLines: [],
            initiateFormAnswers,
          },
          rateSegment,
          InitiateType.REGULAR,
        ),
        updateGroup(buildInputGroupFromGroup(this.groupModule.group)),
      ]);

      runInAction(() => {
        this._periodId = period.data?.initiateProposal?.id || '';
      });
    });
  }

  public onCompleteRedirect(): void {
    redirectToRoute(this.domainData.nextPageId, { ...this.domainData, cartId: this._cartId, periodId: this._periodId });
  }

  public constructor({ domainParams }: InitiateQuoteFlowParams) {
    this._domainData = domainParams;
    this._groupModule = new GroupModule();
    this.groupInfoDelegate = new InitiateQuoteFlowGroupInfoDelegate(this._groupModule);
    this.domainProductLinesDelegate = new DomainProductLinesDelegate({
      rateSegment: 'GROUP',
      // Block quoting installed lines here when change-plan is off. Do not do this on quote proposal info.
      getCoveredProductLineIds: (): ProductLineId[] => {
        return getCoveredProductLines(this._enrollmentPeriods);
      },
    });

    makeAutoObservable(this, { domainProductLinesDelegate: false }, { autoBind: true });
  }
}
